/**
 * EntitySelector
 *
 * @typedef {Object} EntitySelector
 * @property {boolean} open - Determines if the modal is open.
 * @property {() => void} onClose - Function to close the modal.
 * @property {string} title - Title of the modal.
 * @property {any[]} listEntities - List of entities to display.
 * @property {(search: string, data: any) => boolean} filterCriteria - Function to filter the entities based on search criteria.
 * @property {(selected: any) => void} onSelect - Function to handle the selection of an entity (or array when multiSelect).
 * @property {string} keyField - The key field to uniquely identify each entity.
 */

import { BaseSyntheticEvent, FC, memo, useEffect, useMemo, useState } from "react";
import React from "react";
import { Modal } from "components/Modal";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DomainColorSquares } from "components/Requirements";
import { sortByDisplayName } from "utils/Array";

type EntitySelectorWithTabsProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  listEntities: any[];
  filterCriteria: (search: string, data: any) => boolean;
  onSelect: (selected: any) => void;
  keyField: string;
  displayField?: string;
  displayImageField?: string | null;
  displayFieldSecondary?: string | null;
  enableNull?: boolean;
  nullDisplay?: string;
  tabs?: { label: string; filterTabCriteria: (data: any) => boolean }[];
  renderItemPrefix?: (data: any) => React.ReactNode;
  multiSelect?: boolean;
  closeOnSelect?: boolean;
  selectedKey?: string | number | null;
  lockOpen?: boolean;
};

export const EntitySelectorWithTabsComponent: FC<
  EntitySelectorWithTabsProps
> = ({
  open,
  onClose,
  title,
  listEntities,
  filterCriteria,
  onSelect,
  keyField,
  displayField = "name",
  displayImageField = null,
  displayFieldSecondary = null,
  enableNull = false,
  nullDisplay = "Any",
  tabs = [],
  renderItemPrefix,
  multiSelect = false,
  closeOnSelect = true,
  selectedKey = null,
  lockOpen = false,
}) => {
  const [search, setSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedKeys(new Set());
      setSearch("");
    }
  }, [open]);

  useEffect(() => {
    if (!selectedKey || !tabs.length) return;
    const selected = listEntities.find(
      (entity) => String(entity[keyField]) === String(selectedKey),
    );
    if (!selected) return;
    const tabIndex = tabs.findIndex((tab) => tab.filterTabCriteria(selected));
    if (tabIndex >= 0) setActiveTab(tabIndex);
  }, [selectedKey, listEntities, tabs, keyField]);

  const filteredListEntities = useMemo(() => {
    let base = listEntities;
    if (tabs && tabs.length > 0) {
      const currentTab = tabs[activeTab] || tabs[0];
      base = base.filter((data) => currentTab.filterTabCriteria(data));
    }
    const filtered =
      search === ""
        ? base
        : base.filter((data) => filterCriteria(search, data));
    return sortByDisplayName(filtered, displayField);
  }, [search, listEntities, tabs, activeTab, filterCriteria, displayField]);

  const filteredKeys = useMemo(
    () => filteredListEntities.map((data) => String(data[keyField])),
    [filteredListEntities, keyField],
  );

  const allFilteredSelected =
    filteredKeys.length > 0 &&
    filteredKeys.every((key) => selectedKeys.has(key));

  const someFilteredSelected =
    filteredKeys.some((key) => selectedKeys.has(key)) && !allFilteredSelected;

  const handleOnSelect = (data: any) => {
    onSelect(data);
    if (closeOnSelect) onClose();
  };

  const toggleItem = (data: any) => {
    const key = String(data[keyField]);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredKeys.forEach((key) => next.delete(key));
      } else {
        filteredKeys.forEach((key) => next.add(key));
      }
      return next;
    });
  };

  const handleConfirmMultiSelect = () => {
    const selected = listEntities.filter((data) =>
      selectedKeys.has(String(data[keyField])),
    );
    onSelect(selected);
    onClose();
  };

  const renderListItemContent = (data: any) => (
    <>
      {renderItemPrefix ? (
        <ListItemAvatar>{renderItemPrefix(data)}</ListItemAvatar>
      ) : displayImageField && data[displayImageField] ? (
        <ListItemAvatar>
          <Avatar
            src={data[displayImageField]}
            alt={data[displayField]}
            sx={{ width: 40, height: 40 }}
          />
        </ListItemAvatar>
      ) : null}
      <ListItemText
        disableTypography
        primary={
          <Typography variant="body1" component="span">
            {data[displayField]}
          </Typography>
        }
        secondary={
          <Stack spacing={0.5} alignItems="flex-start">
            {displayFieldSecondary ? (
              <Typography variant="body2" color="text.secondary">
                {data[displayFieldSecondary]}
              </Typography>
            ) : null}
            <DomainColorSquares requirement={data} />
          </Stack>
        }
      />
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="small"
      hideCloseButton={lockOpen}
      disableAutoFocus={lockOpen}
    >
      <Box mt={2}>
        {tabs && tabs.length > 0 && (
          <Box mt={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={(_e, newValue) => setActiveTab(newValue)}
              aria-label="filtered tabs"
            >
              {tabs.map((t, idx) => (
                <Tab key={idx} label={t.label} />
              ))}
            </Tabs>
          </Box>
        )}
        <Box mt={2}>
          <TextField
            placeholder="Search"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(e: BaseSyntheticEvent) => setSearch(e.target.value)}
          />
        </Box>
        <Box
          sx={{
            height: 300,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {multiSelect && filteredListEntities?.length > 0 && (
            <List dense disablePadding>
              <ListItem
                disablePadding
                sx={{ borderBottom: "1px solid rgba(0,0,0,0.2)" }}
              >
                <ListItemButton onClick={toggleSelectAll} dense>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox
                      edge="start"
                      checked={allFilteredSelected}
                      indeterminate={someFilteredSelected}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600}>
                        Select all
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </List>
          )}
          {filteredListEntities?.length > 0 && (
            <List dense={multiSelect}>
              {filteredListEntities?.map((data, i) => {
                const key = String(data[keyField]);
                const isChecked = selectedKeys.has(key);

                return (
                  <ListItem
                    key={key}
                    disablePadding
                    sx={{
                      borderBottom:
                        i === filteredListEntities.length - 1 && !enableNull
                          ? "none"
                          : "1px solid rgba(0,0,0,0.2)",
                    }}
                  >
                    <ListItemButton
                      selected={
                        !multiSelect &&
                        selectedKey != null &&
                        key === String(selectedKey)
                      }
                      onClick={() =>
                        multiSelect ? toggleItem(data) : handleOnSelect(data)
                      }
                      dense={multiSelect}
                    >
                      {multiSelect && (
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={isChecked}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                      )}
                      {renderListItemContent(data)}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
          {enableNull && !multiSelect && (
            <ListItem key={"null_element"} disablePadding>
              <ListItemButton onClick={() => handleOnSelect(null)}>
                <ListItemText primary={nullDisplay} />
              </ListItemButton>
            </ListItem>
          )}
          {filteredListEntities?.length === 0 && (
            <Typography variant="body2" align="center" sx={{ margin: 2 }}>
              No results found
            </Typography>
          )}
        </Box>
        {multiSelect && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={selectedKeys.size === 0}
              onClick={handleConfirmMultiSelect}
            >
              Add{selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ""}
            </Button>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export const EntitySelectorWithTabs = memo(EntitySelectorWithTabsComponent);
